import type { PluginContext, PlayerError, StreamSource } from './types';
import { extractQueryParams } from './Config';

const CDN_HOSTS = new Set(['hls.goodgame.ru', 'ix.goodgame.ru']);

/** Renew signed playlists; recover 403 through each CDN's real-IP API at most once. */
export class CdnTokenRecovery {
  private attempted = false;
  private recoveryOrigins: string[] = [];
  private wantsPlayback = false;
  private destroyed = false;
  private request: AbortController | null = null;
  private apiOrigin = 'https://goodgame.ru';
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubs: Array<() => void>;

  constructor(private ctx: PluginContext, private reload: (source: StreamSource, query: string, restart: boolean) => void | Promise<void>) {
    this.unsubs = [
      ctx.eventBus.on('playback:requested', () => { this.wantsPlayback = true; }),
      ctx.eventBus.on('play', () => { this.wantsPlayback = true; }),
      ctx.eventBus.on('playback:cancelled', () => this.cancel()),
      ctx.eventBus.on('error', error => { void this.recover(error); }),
      ctx.eventBus.on('hls:playlist:loaded', () => ctx.store.setState({ tokenRecoveryPending: false })),
    ];
    this.scheduleRefresh();
  }

  cancel(): void {
    this.wantsPlayback = false;
    const hadRequest = this.request !== null;
    this.request?.abort();
    if (!this.destroyed) this.ctx.store.setState({ tokenRecoveryPending: false });
    this.scheduleRefresh(hadRequest ? 30_000 : undefined);
  }

  destroy(): void {
    this.destroyed = true;
    this.cancel();
    this.clearRefreshTimer();
    this.unsubs.forEach(off => off());
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private scheduleRefresh(retryMs?: number): void {
    this.clearRefreshTimer();
    if (this.destroyed || this.ctx.store.getState().accessDenied) return;
    const source = this.ctx.config.sources ?? this.ctx.config.apiData?.sources;
    if (!source || source.mp4) return;
    try {
      const url = new URL(source.master ?? source.source ?? '');
      const expires = Number(url.searchParams.get('expires')) * 1000;
      if (!CDN_HOSTS.has(url.hostname) || url.protocol !== 'https:' || !url.searchParams.has('token') || !Number.isFinite(expires) || expires <= 0) return;
      // Renew 30 seconds early, including initial site-issued tokens with no 403.
      const delay = retryMs ?? Math.max(1_000, expires - Date.now() - 30_000);
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        if (this.ctx.store.getState().accessDenied) return;
        if (this.request) { this.scheduleRefresh(30_000); return; }
        void this.refresh(this.apiOrigin, false);
      }, Math.min(delay, 2_147_483_647));
    } catch { /* Unsigned and non-CDN sources do not need token renewal. */ }
  }

  private async recover(error: PlayerError): Promise<void> {
    if (error.code !== 403 || !error.fatal || this.destroyed) return;
    this.clearRefreshTimer();
    if (this.attempted) {
      if (this.request && this.ctx.store.getState().tokenRecoveryPending) return;
      this.request?.abort();
      await this.tryNextOrigin();
      return;
    }
    const { config } = this.ctx;
    const source = config.sources ?? config.apiData?.sources;
    const original = source?.master ?? source?.source;
    if (!original || source?.mp4) return;
    let playlist: URL;
    try { playlist = new URL(original); } catch { return; }
    if (!CDN_HOSTS.has(playlist.hostname) || playlist.protocol !== 'https:' || !playlist.searchParams.has('token')) return;

    // Prefer the failing request's CDN (redirects may differ from the master).
    const details = error.details as { context?: { url?: string }; frag?: { url?: string }; response?: { url?: string } } | undefined;
    const failedUrl = details?.context?.url ?? details?.response?.url ?? details?.frag?.url;
    if (failedUrl) {
      try {
        const failed = new URL(failedUrl);
        if (CDN_HOSTS.has(failed.hostname) && failed.protocol === 'https:') playlist = failed;
      } catch { /* Keep the master CDN when no request URL is available. */ }
    }
    this.attempted = true;
    this.recoveryOrigins = [playlist.origin, ...Array.from(CDN_HOSTS)
      .map(host => `https://${host}`).filter(origin => origin !== playlist.origin)];
    // A site refresh may be in flight when an expired playlist gets a 403.
    this.request?.abort();
    await this.tryNextOrigin();
  }

  private async tryNextOrigin(): Promise<void> {
    if (this.destroyed) return;
    const origin = this.recoveryOrigins.shift();
    if (!origin) {
      this.ctx.store.setState({ tokenRecoveryPending: false, accessDenied: true, loading: false });
      return;
    }
    this.ctx.store.setState({ tokenRecoveryPending: true, accessDenied: false });
    await this.refresh(origin, true);
  }

  private async refresh(origin: string, restart: boolean): Promise<void> {
    const { config, store, eventBus, logger } = this.ctx;
    const source = config.sources ?? config.apiData?.sources;
    const identifier = config.streamKey || config.apiData?.channel_key || config.apiData?.channel_id;
    if (!source || !identifier || this.destroyed) return;
    const route = /^\d+$/.test(identifier) ? 'id' : 'channel';
    const wasPlaying = store.getState().isPlaying;
    const controller = new AbortController();
    this.request = controller;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 8_000);
    try {
      const response = await fetch(`${origin}/api/4/streams/2/${route}/${encodeURIComponent(identifier)}`, {
        signal: controller.signal,
        cache: 'no-store',
        credentials: origin === 'https://goodgame.ru' ? 'include' : 'omit',
      });
      if (!response.ok) throw new Error(`CDN API HTTP ${response.status}`);
      const data = await response.json() as { online?: boolean; sources?: StreamSource };
      if (this.destroyed || (controller.signal.aborted && !timedOut)) return;
      if (timedOut) throw new Error('Token API timed out');
      if (data.online === false) throw new Error('CDN API reports an offline stream');
      const freshUrl = data.sources?.master ?? data.sources?.source;
      if (!freshUrl) throw new Error('CDN API returned no playlist');
      const fresh = new URL(freshUrl);
      const token = fresh.searchParams.get('token');
      const expires = fresh.searchParams.get('expires');
      if (!token || !expires) throw new Error('CDN API returned no signed playlist');
      if (!restart && (!Number.isFinite(Number(expires)) || Number(expires) * 1000 <= Date.now() + 30_000)) {
        throw new Error('API returned credentials too close to expiration');
      }
      const update = (url: string): string => {
        const updated = new URL(url);
        updated.searchParams.set('token', token);
        updated.searchParams.set('expires', expires);
        return updated.href;
      };
      const updated: StreamSource = {
        ...source,
        ...(source?.master ? { master: update(source.master) } : {}),
        ...(source?.source ? { source: update(source.source) } : {}),
        ...(source?.qualities ? { qualities: Object.fromEntries(Object.entries(source.qualities)
          .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
          .map(([quality, url]) => [quality, update(url)])) } : {}),
      };
      config.sources = updated;
      if (config.apiData) config.apiData.sources = updated;
      this.apiOrigin = origin;
      const resume = this.wantsPlayback || wasPlaying;
      const seamless = !restart && !!this.ctx.getEngine()?.updateSourceAuth;
      if (!seamless) store.setState({ accessDenied: false, loading: resume });
      if (restart && resume) eventBus.emit('playback:recovery', { action: 'cdn_token_refresh', reason: 'potential_ip_mismatch_403', attempt: 2 - this.recoveryOrigins.length });
      clearTimeout(timeout);
      await this.reload(updated, extractQueryParams(updated.master ?? updated.source ?? ''), restart);
      if (controller.signal.aborted || this.destroyed) return;
      if (!seamless && resume) this.ctx.getEngine()?.play();
      this.scheduleRefresh();
    } catch {
      // Keep the existing access-denied state; never loop or claim recovery.
      if (!controller.signal.aborted && !this.destroyed) logger.warn('CDN token refresh failed');
      if (restart && !this.destroyed && (!controller.signal.aborted || timedOut)) await this.tryNextOrigin();
      if (!restart && this.request === controller) this.scheduleRefresh(30_000);
    } finally {
      clearTimeout(timeout);
      if (this.request === controller) this.request = null;
    }
  }
}
